import * as React from 'react';
import { ReleaseCalendarExternalEventData, capitalizeFirstLetter } from '@/helpers';
import { Integration, LifecycleEventType, ReleaseCalendarEventType, ReleaseCalendarViewType } from '@/types';
import Space from 'antd/lib/space';
import Text from 'antd/lib/typography/Text';
import SmallStatusNode from './SmallStatusNode';
import ReleaseCalendarEventPopover from './ReleaseCalendarEventPopover';
import { displayLifecycleEventTime } from '@/helpers/displayHelpers';
import { useRef, useEffect, useState } from 'react';
import { iconAndAltForIntegrationType } from '@/helpers/integrationHelpers';
import { runwayColors } from './styles/globalStyles';

interface ReleaseCalendarExternalEventDataProps {
    event: ReleaseCalendarExternalEventData;
    view: ReleaseCalendarViewType;
    orgId: string;
    appId: string;
    releaseId?: string;
    integration: Integration;
}

export const ReleaseCalendarExternalEvent = (props: ReleaseCalendarExternalEventDataProps) => {
    const event = props.event;
    if (!props.integration) {
        // If integration is not connected, don't try to render the event
        return null;
    }

    let eventTitle = capitalizeFirstLetter(event.title);

    const titleRef = useRef<HTMLDivElement>(null);
    const containerRef = useRef<HTMLDivElement>(null);
    const titlePrefixRef = useRef<HTMLDivElement>(null);
    const [isOverflowing, setIsOverflowing] = useState(false);
    const [titleWidth, setTitleWidth] = useState(0);

    useEffect(() => {
        const containerElement = containerRef.current;
        const titleElement = titleRef.current;
        const titlePrefixElement = titlePrefixRef.current;
        if (!containerElement || !titleElement || !titlePrefixElement) return () => {};

        const observer = new ResizeObserver(() => {
            const hasHorizontalOverflow =
                titlePrefixElement.scrollWidth + titleElement.scrollWidth >= containerElement.clientWidth;
            setIsOverflowing(hasHorizontalOverflow);

            if (hasHorizontalOverflow) {
                setTitleWidth((containerElement.clientWidth - titlePrefixElement.scrollWidth) * 0.8);
            }
        });

        observer.observe(containerElement);

        return () => observer.disconnect();
    }, []);

    const eventContent =
        props.view === ReleaseCalendarViewType.ReleaseCalendarViewTypeMonth ?
            <div
                style={{
                    width: '100%',
                    paddingTop: 1,
                    display: 'flex',
                    gap: '8px',
                    paddingLeft: 5,
                    alignItems: 'center',
                }}
                ref={containerRef}
            >
                <SmallStatusNode color={event.backgroundColor} />
                <div ref={titlePrefixRef}>
                    <Text>{displayLifecycleEventTime(event.start)}</Text>
                </div>
                <div ref={titleRef}>
                    <Text
                        strong
                        style={{
                            maxWidth: '100%',
                            ...(isOverflowing && {
                                display: 'block',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                                width: titleWidth,
                            }),
                        }}
                    >
                        {eventTitle}
                    </Text>
                </div>
            </div>
        : props.view === ReleaseCalendarViewType.ReleaseCalendarViewTypeWeek ?
            <div ref={containerRef} style={{ width: '100%' }}>
                <Space size="small">
                    <div ref={titlePrefixRef}>
                        <Text style={{ color: runwayColors.white, whiteSpace: 'nowrap' }}>
                            {displayLifecycleEventTime(event.start)}
                        </Text>
                    </div>
                    <div ref={titleRef}>
                        <Text
                            style={{
                                color: runwayColors.white,
                                maxWidth: '100%',
                                whiteSpace: 'nowrap',
                                ...(isOverflowing && {
                                    display: 'block',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    width: titleWidth,
                                }),
                            }}
                        >
                            {eventTitle}
                        </Text>
                    </div>
                </Space>
            </div>
        : props.view === ReleaseCalendarViewType.ReleaseCalendarViewTypeDay ?
            <Space size="small">
                <Text style={{ color: runwayColors.white }}>{displayLifecycleEventTime(event.start)}</Text>
                <Text style={{ color: runwayColors.white }}>{eventTitle}</Text>
            </Space>
        :   <Text>{eventTitle}</Text>;
    const [icon, alt] = iconAndAltForIntegrationType(props.integration.id);
    return (
        <ReleaseCalendarEventPopover
            orgId={props.orgId}
            appId={props.appId}
            releaseId={props.releaseId ?? props.appId + event.id}
            backgroundColor={event.backgroundColor}
            displayVersion={''}
            eventContent={eventContent}
            lifecycleAutomations={null}
            eventDateType={ReleaseCalendarEventType.ReleaseCalendarExternalEvent}
            eventType={LifecycleEventType.LifecycleEventTypeExternalEvent}
            isEventComplete={false}
            eventDate={event.start}
            isTargetDateScheduledFromCadence={false}
            incrementDayNumber={0}
            stagedRolloutPercentage={0}
            isSkippedRelease={false}
            title={event.title}
            isExternalEvent={true}
            externalEventDescription={event.description}
            organizer={event.organizer}
            integrationType={props.integration.id}
            titleIcon={<img src={icon} alt={alt} />}
            eventEndDate={event.end}
        />
    );
};
